import { NOTIFICATION_TYPE } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { wipeOfflineData } from '../lib/offline-account';
import { ClubLogo, useClub } from '../lib/club';
import { NOTIFICATION_TYPE_LABELS } from '../lib/format';
import { Amount, useFinanceThresholds } from '../components/Amount';
import { useConfirm } from '../components/ConfirmDialog';
import { PageHeader } from '../components/PageHeader';
import { OfflineCard } from '../components/OfflineCard';
import { PushCard } from '../components/PushCard';
import { ResponsePushCard } from '../components/ResponsePushCard';
import { SystemTab } from '../components/SystemTab';
import { TrainingVideoSettingsCard } from '../components/TrainingVideoSettingsCard';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Field,
  Input,
  LoadingBlock,
  SectionTitle,
  Tabs,
  useToast,
} from '../components/ui';

type Reiter = 'konto' | 'benachrichtigungen' | 'verein' | 'system';

export function SettingsPage(): JSX.Element {
  const { user, can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const zeigeVerein = (['organisation', 'persons', 'permissions', 'tasks', 'calendar'] as const).some((resource) => can(resource, 'read'))
    || can('permissions', 'manage') || can('finance', 'manage');
  // Betrieb nur für Systemverantwortung am Gesamtverein - und nicht in der
  // Testansicht, dort gelten die Rechte der angesehenen Person.
  const zeigeSystem = Boolean(user?.canImpersonate) && !user?.isImpersonating;
  const reiterListe: { id: Reiter; label: string }[] = [
    { id: 'konto', label: 'Mein Konto' },
    { id: 'benachrichtigungen', label: 'Benachrichtigungen' },
    ...(zeigeVerein ? [{ id: 'verein' as const, label: 'Verein' }] : []),
    ...(zeigeSystem ? [{ id: 'system' as const, label: 'System' }] : []),
  ];
  const gewuenscht = params.get('reiter');
  const reiter: Reiter = reiterListe.find((r) => r.id === gewuenscht)?.id ?? 'konto';
  const setReiter = (id: string): void => {
    const naechste = new URLSearchParams(params);
    naechste.set('reiter', id);
    setParams(naechste, { replace: true });
  };

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [settings, setSettings] = useState<Record<string, { inApp: boolean; email: boolean; push: boolean }>>({});
  const [mailPreferences, setMailPreferences] = useState({ dailyDigest: false, calendarChanges: false, mailConfigured: false });

  const { data, isLoading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => api.get<{ type: string; inApp: boolean; email: boolean; push: boolean }[]>('/api/notifications/settings'),
  });
  const { data: operationsPreferences } = useQuery({
    queryKey: ['operations-preferences'],
    queryFn: () => api.get<{ dailyDigest: boolean; calendarChanges: boolean; mailConfigured: boolean }>('/api/operations/preferences'),
  });

  useEffect(() => {
    const next: Record<string, { inApp: boolean; email: boolean; push: boolean }> = {};
    for (const type of NOTIFICATION_TYPE) {
      const existing = data?.find((s) => s.type === type);
      next[type] = { inApp: existing?.inApp ?? true, email: existing?.email ?? false, push: existing?.push ?? true };
    }
    setSettings(next);
  }, [data]);
  useEffect(() => {
    if (operationsPreferences) setMailPreferences(operationsPreferences);
  }, [operationsPreferences]);

  const saveSettings = useMutation({
    mutationFn: () =>
      api.put(
        '/api/notifications/settings',
        Object.entries(settings).map(([type, value]) => ({
          type,
          inApp: value.inApp,
          email: value.email,
          push: value.push,
        })),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      toast.show('Einstellungen gespeichert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  const changePassword = useMutation({
    mutationFn: () => api.post('/api/auth/change-password', { currentPassword, newPassword }),
    onSuccess: () => {
      toast.show('Passwort geändert');
      setCurrentPassword('');
      setNewPassword('');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Änderung fehlgeschlagen', 'error'),
  });
  const saveMailPreferences = useMutation({
    mutationFn: () => api.put('/api/operations/preferences', mailPreferences),
    onSuccess: () => toast.show('E-Mail-Einstellungen gespeichert'),
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'E-Mail-Einstellungen konnten nicht gespeichert werden', 'error'),
  });

  const revokeSessions = useMutation({
    mutationFn: () => api.post('/api/auth/revoke-sessions'),
    onSuccess: () => {
      // Auch die eigene Sitzung ist beendet - zurueck zur Anmeldung. Offline gespeicherte Einheiten gehen mit.
      void wipeOfflineData(user?.userId).catch(() => undefined).finally(() => { window.location.href = '/anmelden'; });
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Abmelden fehlgeschlagen', 'error'),
  });

  if (isLoading) return <LoadingBlock />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Einstellungen"
        description="Konto, Benachrichtigungen und vereinsweite Vorgaben"
        scoped={false}
      />

      <Tabs tabs={reiterListe} active={reiter} onChange={setReiter} />

      {reiter === 'verein' && (
      <div className="space-y-4">
      {(['organisation', 'persons', 'permissions', 'tasks', 'calendar'] as const).some((resource) => can(resource, 'read')) && (
        <Link
          to="/verein"
          aria-label="Verein"
          className="block rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
        >
          <span className="flex items-center justify-between gap-3 font-semibold text-brand-700">
            Verein <span aria-hidden="true">→</span>
          </span>
          <span className="mt-1 block text-sm text-slate-600">Vereinsstruktur, Personen und ihre Zugänge</span>
        </Link>
      )}

      {can('permissions', 'manage') && <VereinslogoCard />}

      {can('finance', 'manage') && <FinanceThresholdCard />}

      {can('permissions', 'manage') && !user?.isImpersonating && <TrainingVideoSettingsCard />}
      </div>
      )}

      {reiter === 'konto' && (
      <div className="space-y-4">

      <OfflineCard />

      <Card>
        <CardBody>
          <SectionTitle title="Mein Konto" />
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Name</dt>
              <dd className="font-medium text-slate-800">{user?.displayName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">E-Mail</dt>
              <dd className="text-slate-800">{user?.email}</dd>
            </div>
          </dl>
          <div className="mt-3 flex flex-wrap gap-1">
            {(user?.roles ?? [])
              .filter((r) => r.isCurrent)
              .map((role) => (
                <Badge key={role.id} tone="brand">
                  {role.roleName} · {role.organisationUnitName}
                </Badge>
              ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle title="Passwort ändern" />
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              changePassword.mutate();
            }}
          >
            <Field label="Aktuelles Passwort" required>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              )}
            </Field>
            <Field label="Neues Passwort" required hint="Mindestens 8 Zeichen">
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              )}
            </Field>
            <Button
              type="submit"
              loading={changePassword.isPending}
              disabled={!currentPassword || newPassword.length < 8}
            >
              Passwort ändern
            </Button>
          </form>

          <div className="mt-4 border-t border-slate-200 pt-4">
            <p className="text-sm font-medium text-slate-700">Angemeldete Geräte</p>
            <p className="hint">
              Meldet dich überall ab – auch auf Geräten, die du nicht mehr hast. Danach ist eine
              neue Anmeldung nötig.
            </p>
            <Button
              variant="secondary"
              className="mt-2"
              loading={revokeSessions.isPending}
              onClick={() => {
                if (window.confirm('Auf allen Geräten abmelden?')) revokeSessions.mutate();
              }}
            >
              Überall abmelden
            </Button>
          </div>
        </CardBody>
      </Card>

      </div>
      )}

      {reiter === 'benachrichtigungen' && (
      <div className="space-y-4">
      <PushCard channel="account" />
      {can('sports_participation', 'read') && <ResponsePushCard />}

      <Card>
        <CardBody>
          <SectionTitle
            title="Benachrichtigungen"
            subtitle="Damit du nicht mit Meldungen überflutet wirst"
          />
          <div className="space-y-2">
            {NOTIFICATION_TYPE.map((type) => (
              <div
                key={type}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2"
              >
                <span className="text-sm text-slate-700">
                  {NOTIFICATION_TYPE_LABELS[type] ?? type}
                </span>
                <Checkbox
                  label="In der App"
                  checked={settings[type]?.inApp ?? true}
                  onChange={(value) =>
                    setSettings((current) => ({
                      ...current,
                      [type]: { inApp: value, email: current[type]?.email ?? false, push: current[type]?.push ?? true },
                    }))
                  }
                />
                <Checkbox
                  label="Per E-Mail"
                  checked={settings[type]?.email ?? false}
                  onChange={(value) =>
                    setSettings((current) => ({
                      ...current,
                      [type]: { inApp: current[type]?.inApp ?? true, email: value, push: current[type]?.push ?? true },
                    }))
                  }
                />
                <Checkbox
                  label="Push"
                  checked={settings[type]?.push ?? true}
                  onChange={(value) =>
                    setSettings((current) => ({
                      ...current,
                      [type]: { inApp: current[type]?.inApp ?? true, email: current[type]?.email ?? false, push: value },
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <Button className="mt-3" onClick={() => saveSettings.mutate()} loading={saveSettings.isPending}>
            Speichern
          </Button>
          <p className="hint mt-2">E-Mail-Versand: {mailPreferences.mailConfigured ? 'konfiguriert' : 'nicht konfiguriert'}</p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle title="E-Mail-Zusammenfassungen" subtitle="Optionale persönliche E-Mail-Einstellungen" />
          <div className="space-y-3">
            <Checkbox label="Tägliche Zusammenfassung ungelesener Meldungen" checked={mailPreferences.dailyDigest} onChange={(value) => setMailPreferences((current) => ({ ...current, dailyDigest: value }))} />
            <Checkbox label="Änderungen an meinen Kalenderterminen" checked={mailPreferences.calendarChanges} onChange={(value) => setMailPreferences((current) => ({ ...current, calendarChanges: value }))} />
            <Button onClick={() => saveMailPreferences.mutate()} loading={saveMailPreferences.isPending}>Speichern</Button>
          </div>
        </CardBody>
      </Card>

      </div>
      )}

      {reiter === 'system' && zeigeSystem && <SystemTab />}
    </div>
  );
}

/**
 * Grenzen der Betragsklassen.
 *
 * Sie bestimmen, was Personen ohne Finanzeinblick statt der Zahl sehen. Was
 * ein „kleiner" Betrag ist, unterscheidet sich von Verein zu Verein - deshalb
 * ist die Einstellung hier und nicht im Code.
 */
function FinanceThresholdCard(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const thresholds = useFinanceThresholds();

  const [medium, setMedium] = useState('');
  const [high, setHigh] = useState('');

  // Erst wenn die gespeicherten Werte da sind, werden die Felder gefuellt.
  useEffect(() => {
    setMedium(String(thresholds.mediumCents / 100));
    setHigh(String(thresholds.highCents / 100));
  }, [thresholds.mediumCents, thresholds.highCents]);

  const mediumEuro = Number(medium);
  const highEuro = Number(high);
  const invalid = !Number.isFinite(mediumEuro) || !Number.isFinite(highEuro) || mediumEuro >= highEuro;

  const save = useMutation({
    mutationFn: () =>
      api.patch('/api/settings/finance', {
        mediumCents: Math.round(mediumEuro * 100),
        highCents: Math.round(highEuro * 100),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings', 'finance'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsorships'] });
      toast.show('Betragsklassen gespeichert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  return (
    <Card>
      <CardBody>
        <SectionTitle
          title="Betragsklassen"
          subtitle="Was Personen ohne Finanzeinblick anstelle des Betrags sehen"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Ab diesem Betrag „mittel“ (EUR)" required>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                step="1"
                value={medium}
                onChange={(e) => setMedium(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Ab diesem Betrag „groß“ (EUR)"
            required
            error={invalid ? 'Muss größer als die untere Grenze sein' : undefined}
          >
            {(id) => (
              <Input
                id={id}
                type="number"
                min="1"
                step="1"
                value={high}
                onChange={(e) => setHigh(e.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600">Vorschau:</span>
          <Amount amount={null} band="GREEN" />
          <Amount amount={null} band="YELLOW" />
          <Amount amount={null} band="ORANGE" />
        </div>

        <Button className="mt-4" onClick={() => save.mutate()} loading={save.isPending} disabled={invalid}>
          Speichern
        </Button>
      </CardBody>
    </Card>
  );
}

/**
 * Vereinslogo.
 *
 * Bewusst einstellbar statt fest eingebaut: dieselbe Anwendung soll für einen
 * anderen Verein laufen können, ohne dass jemand Bilder im Code austauscht.
 */
function VereinslogoCard(): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const club = useClub();
  const [datei, setDatei] = useState<File | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const aktualisieren = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['club'] });
  };

  const hochladen = useMutation({
    mutationFn: async () => {
      const daten = new FormData();
      daten.append('file', datei as File);
      const antwort = await fetch('/api/clubs/logo', {
        method: 'PUT',
        body: daten,
        credentials: 'include',
      });
      if (!antwort.ok) {
        const fehler = (await antwort.json().catch(() => null)) as
          | { error?: { message?: string } }
          | null;
        throw new Error(fehler?.error?.message ?? 'Hochladen fehlgeschlagen');
      }
    },
    onSuccess: async () => {
      await aktualisieren();
      setDatei(null);
      toast.show('Vereinslogo gespeichert');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof Error ? error.message : 'Hochladen fehlgeschlagen', 'error'),
  });

  const entfernen = useMutation({
    mutationFn: () => api.del('/api/clubs/logo'),
    onSuccess: async () => {
      await aktualisieren();
      toast.show('Vereinslogo entfernt');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Nicht möglich', 'error'),
  });

  const logoEntfernen = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Vereinslogo entfernen?',
      message: 'Das Logo verschwindet aus der Kopfzeile, von der Anmeldeseite und aus dem Browsertab.',
      confirmLabel: 'Ja, entfernen',
      tone: 'danger',
    });
    if (ok) entfernen.mutate();
  };

  return (
    <Card>
      <CardBody>
        {confirmDialog}
        <SectionTitle
          title="Vereinslogo"
          subtitle="Erscheint in der Kopfzeile, auf der Anmeldeseite und im Browsertab"
        />

        <div className="flex flex-wrap items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-xl border border-slate-200 p-1">
            <ClubLogo club={club} size={56} />
          </span>
          <div className="min-w-0 flex-1">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              aria-label="Logodatei"
              onChange={(e) => setDatei(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700"
            />
            <p className="hint">PNG, JPEG, WebP oder SVG, höchstens 2 MB. Echte Transparenz in PNG, WebP und SVG bleibt erhalten.</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button disabled={!datei} loading={hochladen.isPending} onClick={() => hochladen.mutate()}>
            Logo speichern
          </Button>
          {club?.hasLogo && (
            <Button
              variant="secondary"
              loading={entfernen.isPending}
              onClick={() => void logoEntfernen()}
            >
              Entfernen
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
